import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import api, { MAX_UPLOAD_BYTES } from '../../lib/api';
import { Avatar, PageHead, Section, Spinner } from '../../components/ui';
import RichTextEditor from '../../components/RichTextEditor';
import ColorField, { cssBackground } from '../../components/ColorField';
import IconColorField from '../../components/IconColorField';
import { stripHtml } from '../../lib/format';
import AssociationTabs from '../../components/AssociationTabs';
import PriceField from '../../components/PriceField';
import AnnounceField from '../../components/AnnounceField';
import StatusPill from '../../components/StatusPill';
import BackCrumb, { useBackTarget } from '../../components/BackCrumb';
import PreviewModal from '../../components/PreviewModal';
import PracticePlayerPage from '../PracticePlayerPage';
import { deletePracticeWithConfirm } from './PracticesAdminPage';
import '../../styles/learn.css';
import { handleHasMembersError } from '../../components/RemoveMember';

/**
 * Practice editor (design/019). Same block model as the Lesson editor but with
 * per-Practice pricing and a Course association widget (many-to-many search
 * across the publisher's own courses).
 */

const ADD = [
  { kind: 'RICH_TEXT', label: '¶ Text' },
  { kind: 'REFLECTION', label: '📝 Reflection' },
  { kind: 'AUDIO', label: '♪ Audio' },
  { kind: 'VIDEO', label: '▶ Video' },
  { kind: 'DOC', label: '📄 Doc' },
  { kind: 'TYPEFORM', label: '🧾 Embed Form', title: 'Embed external form' },
  { kind: 'YOUTUBE', label: '▶ Embed YouTube' },
];
const isMedia = (k) => k === 'AUDIO' || k === 'VIDEO' || k === 'DOC';
const isEmbed = (k) => k === 'TYPEFORM' || k === 'YOUTUBE';
const TYPES = ['TEXT', 'AUDIO', 'VIDEO', 'DOC'];

export default function PracticeEditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  const [blocks, setBlocks] = useState(null);
  const [busy, setBusy] = useState(false);
  const [announcedAt, setAnnouncedAt] = useState(null);  // design/035
  const [previewing, setPreviewing] = useState(false);
  // JSON snapshot of the last loaded/saved state — drives Cancel's
  // "discard unsaved changes?" confirm.
  const [saved, setSaved] = useState(null);
  // Icon image (optional — shown in the tile instead of the icon color). Uploaded immediately.
  const coverRef = useRef(null);
  const [hasCover, setHasCover] = useState(false);
  const [coverBusy, setCoverBusy] = useState(false);
  const [coverVer, setCoverVer] = useState(0);
  const onCoverFile = async (e) => {
    const file = e.target.files?.[0]; if (e.target) e.target.value = '';
    if (!file) return;
    setCoverBusy(true);
    try { await api.adminUploadPracticeCover(id, file); setHasCover(true); setCoverVer((v) => v + 1); }
    catch (err) { alert(err.message); }
    finally { setCoverBusy(false); }
  };
  const removeCover = async () => {
    if (!window.confirm('Remove the cover image (revert to the color)?')) return;
    setCoverBusy(true);
    try { await api.adminDeletePracticeCover(id); setHasCover(false); }
    catch (err) { alert(err.message); }
    finally { setCoverBusy(false); }
  };
  // `?created=1` — set by "+ New practice". A never-saved draft is deleted
  // on Cancel; the first successful save clears the marker.
  const [params, setParams] = useSearchParams();
  const back = useBackTarget({ fallback: { path: '/admin/practices', label: 'Manage practices' } });
  const justCreated = params.get('created') === '1';

  useEffect(() => {
    if (meta && blocks && saved == null) setSaved(JSON.stringify({ meta, blocks }));
  }, [meta, blocks, saved]);

  useEffect(() => {
    let cancelled = false;
    Promise.all([api.adminGetPractice(id), api.adminGetPracticeBlocks(id)])
      .then(([p, bs]) => {
        if (cancelled) return;
        setMeta({
          title: p.title || '', subtitle: p.subtitle || '', status: p.status || 'DRAFT',
          primaryType: p.primaryType || 'TEXT',
          durationSeconds: p.durationSeconds ?? null,
          headerColor: p.headerColor || '',
          iconColor: p.iconColor || '',
          priceCents: p.priceCents || 0,
          currency: p.currency || 'usd',
          announce: p.announce !== false,
        });
        setAnnouncedAt(p.announcedAt || null);
        setHasCover(!!p.coverImageUri);
        setBlocks((Array.isArray(bs) ? bs : []).map((b) => ({
          kind: b.kind, text: b.text || '', mediaUri: b.mediaUri || null,
          mediaContentType: b.mediaContentType || null, mediaFilename: b.mediaFilename || null,
        })));
      })
      .catch((e) => { if (!cancelled) { alert(e.message); setBlocks([]); setMeta({}); } });
    return () => { cancelled = true; };
  }, [id]);

  const setM = (k) => (e) => setMeta((m) => ({ ...m, [k]: e.target.value }));
  const update = (i, patch) => setBlocks((bs) => bs.map((b, j) => (j === i ? { ...b, ...patch } : b)));
  const remove = (i) => setBlocks((bs) => bs.filter((_, j) => j !== i));
  const move = (i, dir) => setBlocks((bs) => {
    const j = i + dir;
    if (j < 0 || j >= bs.length) return bs;
    const copy = [...bs];
    [copy[i], copy[j]] = [copy[j], copy[i]];
    return copy;
  });
  const add = (kind) => setBlocks((bs) => [...bs, { kind, text: '', mediaUri: null, mediaContentType: null, mediaFilename: null }]);

  const newFileRef = useRef(null);
  const [addKind, setAddKind] = useState(null);
  const [addingMedia, setAddingMedia] = useState(false);
  const ACCEPT = { AUDIO: 'audio/*', VIDEO: 'video/*', DOC: '.pdf,.docx,.txt,.md,.csv,image/*' };

  const startAdd = (kind) => {
    if (!isMedia(kind)) { add(kind); return; }
    setAddKind(kind);
    if (newFileRef.current) { newFileRef.current.value = ''; newFileRef.current.click(); }
  };

  const onNewFile = async (e) => {
    const file = e.target.files?.[0]; e.target.value = '';
    if (!file || !addKind) return;
    setAddingMedia(true);
    try {
      const m = await api.adminUploadLearnMediaSmart(file);   // reuse /admin/learn/media
      setBlocks((bs) => [...bs, { kind: addKind, text: '', mediaUri: m.uri, mediaContentType: m.contentType, mediaFilename: m.filename }]);
    } catch (err) { alert(err.message); }
    finally { setAddingMedia(false); setAddKind(null); }
  };

  const persist = async (statusOverride) => {
    if (!meta?.title?.trim()) { alert('Title is required.'); return false; }
    const status = statusOverride || meta.status;
    setBusy(true);
    try {
      await api.adminUpdatePractice(id, {
        title: meta.title,
        subtitle: meta.subtitle,
        status,
        primaryType: meta.primaryType,
        durationSeconds: meta.durationSeconds,
        headerColor: meta.headerColor || null,
        iconColor: meta.iconColor || '',
        priceCents: Number(meta.priceCents) || 0,
        currency: meta.currency || 'usd',
        announce: meta.announce !== false,
      });
      await api.adminSetPracticeBlocks(id, blocks.map((b) => ({
        kind: b.kind, text: b.text || null, mediaUri: b.mediaUri,
        mediaContentType: b.mediaContentType, mediaFilename: b.mediaFilename,
      })));
      if (statusOverride) setMeta((m) => ({ ...m, status }));
      setSaved(JSON.stringify({ meta: { ...meta, status }, blocks }));
      if (justCreated) {
        const p = new URLSearchParams(params); p.delete('created');
        setParams(p, { replace: true });
      }
      return true;
    } catch (e) { if (!handleHasMembersError(e, 'practices', id, navigate)) alert(e?.data?.message || e.message); return false; }
    finally { setBusy(false); }
  };

  const save = async () => { if (await persist()) alert('Saved.'); };
  // Preview: save silently, then show the learner view in a modal.
  const openPreview = async () => { if (await persist()) setPreviewing(true); };
  const togglePublish = () => {
    if (meta.status === 'PUBLISHED') {
      if (!window.confirm('Unpublish? It will be hidden from members until you publish it again.')) return;
      return persist('DRAFT');
    }
    return persist('PUBLISHED');
  };

  if (blocks == null || meta == null) return <div className="page"><Spinner /></div>;

  const backToList = () => navigate(back.path);
  const cancel = async () => {
    const dirty = saved != null && JSON.stringify({ meta, blocks }) !== saved;
    if (justCreated) {
      if (dirty && !window.confirm('Discard this new practice?')) return;
      setBusy(true);
      const ok = await deletePracticeWithConfirm(id);
      setBusy(false);
      if (ok) backToList();
      return;
    }
    if (dirty && !window.confirm('Discard unsaved changes?')) return;
    backToList();
  };
  // Only the author (or superadmin) can delete; the server refuses while
  // anyone is enrolled (HAS_MEMBERS → offer Manage members).
  const deletePractice = async () => {
    if (!window.confirm(`Delete "${meta.title || 'this practice'}"? This cannot be undone.`)) return;
    setBusy(true);
    const ok = await deletePracticeWithConfirm(id);
    setBusy(false);
    if (ok) backToList();
  };
  const published = meta.status === 'PUBLISHED';
  const priceInputValue = meta.priceCents == null ? '' : (meta.priceCents / 100).toFixed(2);

  return (
    <div className="page">
      <PageHead
        breadcrumb={<BackCrumb to={back} />}
        title={meta.title || 'Practice'}
        sub="Edit this practice's details and content"
        actions={
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <StatusPill draft={!published} />
            <button className="btn" disabled={busy} onClick={cancel}>Cancel</button>
            <button className="btn" disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save'}</button>
            <button className="btn" disabled={busy} onClick={openPreview}>Preview</button>
            <button className={`btn ${published ? '' : 'btn-primary'}`} disabled={busy} onClick={togglePublish}>
              {published ? 'Unpublish' : 'Publish'}
            </button>
          </div>
        }
      />

      {previewing && (
        <PreviewModal kind="practice" onClose={() => setPreviewing(false)}>
          <PracticePlayerPage preview />
        </PreviewModal>
      )}

      <Section title="Practice details">
        <div className="form-field"><label>Cover color</label>
          <ColorField value={meta.headerColor} onChange={(hex) => setMeta((m) => ({ ...m, headerColor: hex }))} />
        </div>
        <IconColorField value={meta.iconColor} coverValue={meta.headerColor}
          onChange={(v) => setMeta((m) => ({ ...m, iconColor: v }))} />
        <div className="form-field">
          <label>Icon image (optional — shown instead of the icon color)</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <Avatar name={meta.title} colorSeed={id} square size={48}
              src={hasCover ? `${api.practiceCoverUrl(id)}?v=${coverVer}` : null}
              bg={cssBackground(meta.iconColor || meta.headerColor)} />
            <input ref={coverRef} type="file" accept="image/png,image/jpeg,image/webp" style={{ display: 'none' }} onChange={onCoverFile} />
            <button type="button" className="btn btn-sm" disabled={coverBusy} onClick={() => coverRef.current?.click()}>
              {coverBusy ? 'Uploading…' : (hasCover ? 'Replace image' : 'Upload image')}
            </button>
            {hasCover && (
              <button type="button" className="btn btn-sm btn-danger" disabled={coverBusy} onClick={removeCover}>Remove</button>
            )}
          </div>
        </div>
        <div className="form-field"><label>Title <span className="req">*</span></label>
          <input className="input" value={meta.title} onChange={setM('title')} /></div>
        <div className="form-field"><label>Subtitle</label>
          <input className="input" value={meta.subtitle} onChange={setM('subtitle')} /></div>
        <div className="form-field" style={{ maxWidth: 200 }}><label>Primary type</label>
          <select className="input" value={meta.primaryType} onChange={setM('primaryType')}>
            {TYPES.map((t) => <option key={t}>{t}</option>)}
          </select>
        </div>
        <PriceField cents={meta.priceCents}
          onChangeCents={(v) => setMeta((m) => ({ ...m, priceCents: v }))}
          freeHint="$0 = free (self-enroll)." />
        <AnnounceField kind="practice" announce={meta.announce} announcedAt={announcedAt}
          onChange={(v) => setMeta((m) => ({ ...m, announce: v }))} />
      </Section>

      <AssociationTabs sourceKind="practice" sourceId={id} />

      <Section title="Content">
        {blocks.length === 0 && <div style={{ color: 'var(--muted)' }}>No blocks yet — add one from the toolbar below.</div>}
        {blocks.map((b, i) => (
          <BlockEditor key={i} block={b} index={i} total={blocks.length}
            onChange={(patch) => update(i, patch)} onRemove={() => remove(i)} onMove={(d) => move(i, d)} />
        ))}
      </Section>

      <Section title="Add a block">
        <input ref={newFileRef} type="file" style={{ display: 'none' }}
          accept={addKind ? ACCEPT[addKind] : undefined} onChange={onNewFile} />
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {ADD.map((a) => (
            <button key={a.kind} className="btn btn-indigo" title={a.title || undefined}
              style={{ fontSize: '0.9375rem', padding: '10px 18px', fontWeight: 600 }}
              disabled={addingMedia} onClick={() => startAdd(a.kind)}>+ {a.label}</button>
          ))}
          {addingMedia && <span style={{ alignSelf: 'center', fontSize: '0.8125rem', color: 'var(--muted)' }}>Uploading…</span>}
        </div>
      </Section>

      {!justCreated && (
        <Section title="Delete practice">
          <div className="danger-zone">
            <p style={{ marginTop: 0, color: 'var(--muted)', fontSize: '0.875rem' }}>
              Deletes the practice and its content. Anyone enrolled must be removed first in Manage members.
            </p>
            <button className="btn btn-danger" onClick={deletePractice}>Delete practice</button>
          </div>
        </Section>
      )}
    </div>
  );
}

// -------- Course association widget --------
//
// CourseAssociationSection retired (design/023) — replaced by the reusable
// <AssociationTabs sourceKind="practice" /> which covers all four kinds
// through the unified /admin/associations backend.

// -------- Block editor (adapted from LessonEditorPage) --------

function BlockEditor({ block, index, total, onChange, onRemove, onMove }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const isAV = block.kind === 'AUDIO' || block.kind === 'VIDEO';

  const onPick = async (e) => {
    const file = e.target.files?.[0]; e.target.value = '';
    if (!file) return;
    const large = file.size > MAX_UPLOAD_BYTES;
    setUploading(true);
    try {
      const m = large ? await api.adminUploadLearnMediaLarge(file) : await api.adminUploadLearnMedia(file);
      onChange({ mediaUri: m.uri, mediaContentType: m.contentType, mediaFilename: m.filename });
    } catch (err) { alert(err.message); }
    finally { setUploading(false); }
  };

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 12, marginBottom: 8, background: 'var(--card-soft)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <span style={{ fontSize: '0.625rem', textTransform: 'uppercase', letterSpacing: 1, color: 'var(--muted)', fontWeight: 700 }}>{block.kind === 'TYPEFORM' ? 'EMBEDDED FORM' : block.kind}</span>
        <span style={{ display: 'flex', gap: 6 }}>
          <button className="btn btn-sm" disabled={index === 0} onClick={() => onMove(-1)}>↑</button>
          <button className="btn btn-sm" disabled={index === total - 1} onClick={() => onMove(1)}>↓</button>
          <button className="btn btn-sm btn-danger" onClick={onRemove}>✕</button>
        </span>
      </div>
      {isMedia(block.kind) ? (
        <div>
          <input ref={fileRef} type="file" style={{ display: 'none' }} onChange={onPick}
            accept={block.kind === 'AUDIO' ? 'audio/*' : block.kind === 'VIDEO' ? 'video/*' : '.pdf,.docx,.txt,.md,.csv'} />
          <div style={{ fontSize: '0.8125rem', color: 'var(--ink-3)', marginBottom: 8 }}>
            {block.mediaFilename ? `📎 ${block.mediaFilename}` : 'No file attached.'}
          </div>
          <button className="btn btn-sm" disabled={uploading} onClick={() => fileRef.current?.click()}>
            {uploading ? 'Uploading…' : (block.mediaFilename ? 'Replace file' : 'Upload file')}
          </button>
        </div>
      ) : isEmbed(block.kind) ? (
        <EmbedField block={block} onChange={onChange} />
      ) : (
        <RichTextEditor value={block.text} onChange={(html) => onChange({ text: html })}
          placeholder={block.kind === 'REFLECTION' ? 'Reflection prompt…' : 'Body text…'} />
      )}
    </div>
  );
}

/**
 * Simple URL input for TYPEFORM / YOUTUBE embed blocks. The URL goes into
 * the block's {@code text} field; the reader parses it into an iframe.
 */
function EmbedField({ block, onChange }) {
  const isYoutube = block.kind === 'YOUTUBE';
  const placeholder = isYoutube
    ? 'https://www.youtube.com/watch?v=…  (or a bare video ID)'
    : 'https://tally.so/r/… or https://…typeform.com/to/… (any form URL)';
  return (
    <div>
      <input
        className="input"
        value={block.text || ''}
        onChange={(e) => onChange({ text: e.target.value })}
        placeholder={placeholder}
      />
      <div style={{ fontSize: '0.75rem', color: 'var(--muted)', marginTop: 6 }}>
        {isYoutube
          ? 'Paste a YouTube link. Both youtube.com and youtu.be links work.'
          : 'Paste any form URL — Typeform, Tally, Google Forms, Jotform. The reader iframes it inline.'}
      </div>
    </div>
  );
}
